/*
 * Session state: the token, and what the API says about who holds it.
 *
 * **Where the token is kept, and what that costs.** sessionStorage — scoped to one tab, cleared
 * when it closes, and it survives a reload. The alternative, memory only, is stronger against
 * XSS in the narrow sense that a script cannot read a variable it has no reference to; it is
 * weaker in practice, because a script running in the page can call the API with the in-memory
 * token anyway, and because losing the session on every reload pushes people toward "remember me"
 * features that are worse than this.
 *
 * The real answer is a rotating refresh token in an httpOnly cookie, which needs the API to hold
 * server-side session state. It is not built and it is not pretended: recorded as a deferral in
 * docs/status/build-status.md rather than left as an assumption in a comment nobody reads.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, createApiClient } from '@indovation/contracts';
import type { AccessMeReadResponse, ApiClient } from '@indovation/contracts';
import { isActionPermitted } from '@indovation/policy-client';
import { config } from '../config.js';
import type { TokenSet } from './oidc.js';
import { forgetProviderRefresh, renewWithProvider } from './oidc.js';
import {
  accessTokenForRequest,
  clearAccessToken,
  clearPasswordSessionMark,
  mayHavePasswordSession,
  renewSession,
  signOutSession,
} from './password.js';

const TOKEN_KEY = 'tf.auth.tokens';

export function readStoredTokens(): TokenSet | null {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (!raw) {
    return null;
  }
  try {
    const tokens = JSON.parse(raw) as TokenSet;
    // An expired token is treated as absent rather than handed out and rejected. The API would
    // answer 401 either way; failing here keeps the reason legible.
    return tokens.expiresAt > Date.now() ? tokens : null;
  } catch {
    return null;
  }
}

export function storeTokens(tokens: TokenSet): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Forget the credential and send the holder back through sign-in — 30 Sep 2026.
 *
 * ── The hole this closes ──────────────────────────────────────────────────────────────────────
 *
 * The bootstrap below has ended a lapsed session properly since M-21.3, and its comment says why:
 * *"a session that lapsed while somebody was reading a client record left every detail on screen
 * while each call came back 401, with no explanation and no way forward."* That fix only ever
 * covered `access.me.read`. Every **other** call in the app — all ninety-odd of them — went
 * straight to the screen that made it, so the same failure came back through a different door: a
 * recruiter uploading a job description got the developer's sentence, "The credential is not valid
 * for this route", printed in a red banner, and no way forward from there either.
 *
 * So it is applied where every call passes rather than where one does.
 *
 * Navigating rather than re-rendering is deliberate: the tokens are gone, half the screen is
 * rendered from data the session can no longer refetch, and a full load is the only thing that
 * puts the app back in a state that matches what the server will now answer.
 */
function endSession(): void {
  clearTokens();
  clearAccessToken();
  // The provider's refresh token too, or the next call would renew a session somebody just ended.
  forgetProviderRefresh();
  // The refresh cookie has just been proved dead. Leaving the marker set would make the next cold
  // load ask again and collect another 401 for nothing.
  clearPasswordSessionMark();

  // Already there: redirecting would replace the reason with a second copy of itself, and on a
  // failed sign-in attempt it would discard the message the screen is showing.
  if (window.location.pathname.startsWith('/sign-in')) {
    return;
  }

  // The destination is preserved and the reason is named, so the screen can say "sign in to
  // continue" rather than appearing to have forgotten them.
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/sign-in?reason=session_expired&returnTo=${encodeURIComponent(returnTo)}`);
}

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface Session {
  readonly status: SessionStatus;
  /** What GET /v1/me returned: tenant, membership, role, record scope, entitlements. */
  readonly me: AccessMeReadResponse | null;
  readonly api: ApiClient;
  /** Epoch milliseconds this tab's session began, or null when nobody is signed in. */
  readonly signedInAt: number | null;
  readonly signOut: () => void;
  /**
   * Called by the callback screen once tokens exist, to load the principal.
   *
   * **Returns the status it settled on** — M-21.8. It returned void, and the callback therefore
   * could not tell the two outcomes apart: a provider that authenticated somebody we do not
   * recognise looks exactly like a provider that authenticated somebody we do. That distinction is
   * the whole of the social-join refusal — `openid email` from Google is proof of an inbox, not of
   * an invitation — and the screen has to say *ask your workspace admin* rather than bounce them to
   * a sign-in form they have just used successfully.
   */
  readonly refresh: () => Promise<SessionStatus>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [me, setMe] = useState<AccessMeReadResponse | null>(null);
  const [signedInAt, setSignedInAt] = useState<number | null>(null);

  // One client for the app's lifetime. `getToken` reads the current credential on every call
  // rather than capturing a value, so a sign-out takes effect immediately on requests already in
  // flight — and so a renewal reaches calls that were already queued behind it.
  const api = useMemo(() => {
    const client = createApiClient({
      baseUrl: config.apiUrl,
      /*
       * Two sources, and the in-memory one wins — M-21.3.
       *
       * A password sign-in keeps its access token in memory (never sessionStorage), and the
       * provider redirect flow still stores a TokenSet. Whichever is present answers; where both
       * are, the memory one is the newer, because it is renewed and the stored one never changes
       * after the callback wrote it.
       *
       * ── The renewal that was written and never wired up — 30 Sep 2026 ─────────────────────
       *
       * This used to read `currentAccessToken()` directly. `accessTokenNeedsRenewal` existed,
       * documented itself as the check to make *before the next call*, and had **no callers
       * anywhere in the repository** — so the five-minute token was handed out until it died,
       * and from that moment every request in the app answered 401. `accessTokenForRequest`
       * is that check, applied.
       */
      getToken: async () => {
        // The password door first: it renews from an httpOnly cookie and needs no provider.
        const own = await accessTokenForRequest();
        if (own) return own;

        const stored = readStoredTokens();
        if (stored) return stored.accessToken;

        /*
         * ── The provider door, renewed — 01 Oct 2026 ────────────────────────────────────────
         *
         * `readStoredTokens` drops an expired token, so reaching here means a provider session
         * whose access token has run out. Until today that was the end of it: the call went out
         * with no credential, came back 401, and somebody working in the product was sent to sign
         * in — which is what happened, mid-edit, and was reported as exactly that.
         *
         * The standard the product owner set, and it is the right one: *"the login cannot abruptly
         * close."* The refresh token obtained at sign-in now buys a new access token with nobody
         * seeing anything.
         */
        const renewed = await renewWithProvider();
        if (renewed) {
          storeTokens(renewed);
          return renewed.accessToken;
        }

        return null;
      },
    });

    /*
     * A 401 on any call gets one renewal and one retry before it reaches a screen.
     *
     * Renewing ahead of the call handles expiry, which is the ordinary case; this handles the rest
     * — a clock that drifted, a token revoked from another tab, a renewal that lost a race. Only
     * when the renewal itself comes back with nothing is the session really over, and then the
     * holder is sent to sign in rather than left reading a sentence written for a developer.
     *
     * Retrying is safe because a 401 is refused at the edge: the route never ran, so there is
     * nothing to have happened twice.
     */
    const call: ApiClient['call'] = async (operationId, input) => {
      try {
        return await client.call(operationId, input);
      } catch (error) {
        if (!(error instanceof ApiError) || !error.isUnauthenticated) {
          throw error;
        }

        /*
         * Both doors, cheapest first — 01 Oct 2026.
         *
         * The cookie needs no round trip to the provider; the provider's refresh token is the
         * fallback for a session that arrived through it. This path tried only the first, so a
         * provider session reaching a 401 was ended here however much life the refresh token had
         * left. That is the abrupt close that was reported.
         */
        const viaCookie = mayHavePasswordSession() ? await renewSession() : null;
        const viaProvider = viaCookie ? null : await renewWithProvider();
        if (viaProvider) storeTokens(viaProvider);

        if (!viaCookie && !viaProvider) {
          endSession();
          throw error;
        }

        return client.call(operationId, input);
      }
    };

    return { call };
  }, []);

  const refresh = useCallback(async () => {
    const stored = readStoredTokens();

    if (!stored) {
      /*
       * ── Nothing in storage is not the same as nobody signed in — fixed 11 Sep 2026 ──────────
       *
       * This used to conclude `anonymous` here and return, without calling anything. That was
       * correct while the only way in was the OIDC flow, which writes its tokens to storage. It
       * stopped being correct the moment M-21.3 added password sign-in, and nobody noticed because
       * the two halves were built and verified separately.
       *
       * A password sign-in keeps its access token **in memory only** — deliberately, because a
       * token in storage is a token any injected script can read. It then navigates with a full
       * page load, which is also deliberate. Those two decisions together mean that by the time
       * this provider mounts, the access token is gone and storage is empty.
       *
       * So every password sign-in did this: 201 from the API, a real session row, a valid refresh
       * cookie sitting in the browser — and then this branch declared the visitor anonymous
       * **without making a single request**, and the guard bounced them straight back to the
       * sign-in screen. Indistinguishable, from the outside, from a rejected password.
       *
       * The refresh cookie is httpOnly, so asking the API is the only way to know whether one is
       * there. That is what this does now. A failure is an ordinary answer — most visitors really
       * are anonymous — so it resolves to null rather than throwing.
       */
      /*
       * Only ask when a password sign-in happened in this browser.
       *
       * Asking unconditionally is what the first version of this fix did, and it put a 401 in front
       * of every anonymous visitor and — more visibly — two of them immediately after a clean sign
       * out. The marker is not a credential and is not trusted: it decides whether the question is
       * worth asking, and the server still decides the answer.
       */
      const renewed = mayHavePasswordSession() ? await renewSession() : null;

      if (!renewed) {
        // Whatever the marker said, there is no session now — so it stops saying otherwise.
        clearPasswordSessionMark();
        setMe(null);
        setSignedInAt(null);
        setStatus('anonymous');
        return 'anonymous';
      }

      // A cookie-backed session has no stored `signedInAt`; the session row owns that fact and
      // `access.me.read` below is what reports it.
      setSignedInAt(null);
    } else {
      // Read from storage rather than captured at sign-in, so a reload keeps the right answer: the
      // callback screen runs once, and every later mount of this provider has only the token to go on.
      setSignedInAt(stored.signedInAt ?? null);
    }

    /*
     * **A failed call is not the same as a rejected credential**, and treating them alike was a
     * real bug rather than a theoretical one.
     *
     * This used to catch everything and clear the token. So a timeout, a dropped connection, a
     * 502 from a proxy or a momentarily-busy API all signed the recruiter out — losing whatever
     * they were part-way through, with no message and nothing in any log to say why. It surfaced
     * as an intermittent across three unrelated browser specs, each showing the sign-in screen
     * mid-test, and it got easier to hit once the relay started polling and the API had steady
     * background load.
     *
     * Only the server saying *no* ends a session. 401 is an expired or revoked credential and 403
     * is a principal whose membership no longer authorises them; both mean "not signed in" and
     * the token is dropped so the next render does not retry with it. Anything else means the
     * question was not answered, and the honest response to an unanswered question is to ask it
     * again rather than to conclude the worst.
     */
    for (let attempt = 1; ; attempt += 1) {
      try {
        setMe(await api.call('access.me.read'));
        setStatus('authenticated');
        return 'authenticated';
      } catch (error) {
        // 401 only. `ApiError.isForbidden`'s own comment is the argument: "the credential was
        // fine and the answer is still no — signing in again changes nothing", so a 403 must not
        // destroy a working token. It is still not a session, and it is still not worth retrying,
        // because a definitive refusal does not become an acceptance on the second ask.
        if (error instanceof ApiError && error.isUnauthenticated) {
          /*
           * **The session is over, so leave the page** — M-21.3, and this was a live defect.
           *
           * This used to clear the tokens and set status, and nothing navigated. The guard
           * bounces an anonymous visitor, but the guard only runs on a route change — so a
           * session that lapsed while somebody was reading a client record left every detail
           * on screen while each call came back 401, with no explanation and no way forward.
           *
           * One renewal is attempted first, and that ordering is the whole design: the access
           * token is five minutes and expiring is its NORMAL end, not a sign-out. Only a
           * failed refresh — idle window passed, absolute window passed, token revoked — is a
           * real expiry. Redirecting without trying would bounce everybody every five minutes.
           */
          /*
           * Both doors. The cookie is cheap and needs no provider; the provider's refresh token is
           * the fallback for a session that arrived through it, and before 01 Oct a provider
           * session simply ended here.
           */
          const renewed = (await renewSession()) ?? (await renewWithProvider().then((set) => {
            if (set) storeTokens(set);
            return set ? { accessToken: set.accessToken, expiresIn: 0 } : null;
          }));
          if (renewed) {
            setMe(await api.call('access.me.read'));
            setStatus('authenticated');
            return 'authenticated';
          }

          setMe(null);
          setStatus('anonymous');
          endSession();
          return 'anonymous';
        }

        if ((error instanceof ApiError && error.isForbidden) || attempt >= 3) {
          // Refused on authorization, or out of attempts. Either way the token is deliberately
          // *kept*: nothing has said the credential is bad, so a reload once the API answers
          // again signs the recruiter straight back in rather than making them repeat a login
          // they never actually lost.
          setMe(null);
          setStatus('anonymous');
          return 'anonymous';
        }

        await new Promise((resolve) => setTimeout(resolve, attempt * 250));
      }
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * Signing out sends you to the sign-in screen with **no destination remembered**, and that is the
   * whole fix.
   *
   * Before this it cleared the tokens and left the browser on whatever page you were reading. The
   * guard then did its job — bounce an anonymous visitor to `/sign-in?returnTo=<here>` — and the
   * next sign-in put you straight back on that page. So "sign out" behaved like "lock the screen",
   * and a user who signed out of a client record signed back in to that client record.
   *
   * A deliberate sign-out is not a deep link. The guard's behaviour is right for the cases it was
   * written for — an expired session, or somebody opening a shared URL — and both still land where
   * they were going, because in those cases the user *did* ask for a destination.
   *
   * `window.location.assign`, not the router: the whole point is to leave the authenticated tree,
   * and a full load also drops every cached query belonging to the person signing out.
   */
  const signOut = useCallback(() => {
    // The server session is ended first, so the refresh cookie stops working for everyone
    // holding it. Clearing only this tab would leave a month-long credential live.
    void signOutSession().finally(() => {
      clearTokens();
      clearAccessToken();
      setMe(null);
      clearPasswordSessionMark();
      setStatus('anonymous');
      window.location.assign('/sign-in');
    });
  }, []);

  const value = useMemo<Session>(
    () => ({ status, me, api, signedInAt, signOut, refresh }),
    [status, me, api, signedInAt, signOut, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession was called outside a <SessionProvider>.');
  }
  return session;
}

/**
 * Arch §4.3 layer 2, on the client.
 *
 * The server is the authority — every route re-checks entitlement, and a screen that got here by
 * typing a URL still gets a 403. This exists so the UI does not offer what the tenant has not
 * bought, which is a usability rule, not a security one. Writing that down matters: the moment
 * someone believes this *is* the check, the server-side one starts looking redundant.
 */
export function hasEntitlement(me: AccessMeReadResponse | null, key: string | null): boolean {
  if (key === null) {
    return true;
  }
  return me?.entitlements.includes(key) ?? false;
}

/**
 * Arch §4.3 layer 3, on the client.
 *
 * Reads the same matrix the API decides with — that is the whole reason `@indovation/policy-client`
 * is a package and not a file inside `access`. A second copy of "who may add a client" in the UI
 * is a copy that drifts, and the drift is silent in the direction that matters: a button that
 * should not be there gets clicked, and the user meets a 403 they were invited to trigger.
 *
 * As with entitlements, this decides what to *offer*, never what to *allow*. The server re-checks
 * every request, and typing the URL still earns a 403.
 */
export function can(
  me: AccessMeReadResponse | null,
  action: string,
  resource: string,
): boolean {
  if (!me) {
    return false;
  }
  return isActionPermitted({ roleKey: me.membership.role_key, action, resource }).allowed;
}
